import axios from "axios";

const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);
const RETRY_ATTEMPTS = 3;
const REQUEST_GAP_MS = 225;

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let pacingTail = Promise.resolve();
let nextRequestAt = 0;

// All Markets panels share this start-time queue. Requests can still run
// concurrently, but their launch times stay below the API gateway's burst
// threshold instead of every card firing in the same millisecond.
const paced = (request) => {
  const slot = pacingTail.then(async () => {
    const delay = Math.max(0, nextRequestAt - Date.now());
    if (delay) await pause(delay);
    nextRequestAt = Date.now() + REQUEST_GAP_MS;
  });
  pacingTail = slot.catch(() => {});
  return slot.then(request);
};

const backoffMs = (attempt) =>
  850 * (attempt + 1) + Math.round(Math.random() * 300);

export async function marketGet(url, config = {}) {
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await paced(() => axios.get(url, {
        timeout: 15000,
        ...config,
      }));
    } catch (error) {
      const status = error?.response?.status;
      const cancelled = error?.code === "ERR_CANCELED";
      const retryable = !cancelled && (!status || RETRYABLE_STATUS.has(status));
      if (!retryable || attempt === RETRY_ATTEMPTS - 1) throw error;
      await pause(backoffMs(attempt));
    }
  }
  throw new Error(`Market request failed: ${url}`);
}

export async function marketFetchJson(url, options = {}) {
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt += 1) {
    try {
      const response = await paced(() => fetch(url, options));
      if (!response.ok) {
        const error = new Error(`Market feed unavailable (${response.status})`);
        error.status = response.status;
        throw error;
      }
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Market feed returned an invalid response");
      }
      return await response.json();
    } catch (error) {
      const cancelled = error?.name === "AbortError";
      const retryable = !cancelled && (!error?.status || RETRYABLE_STATUS.has(error.status));
      if (!retryable || attempt === RETRY_ATTEMPTS - 1) throw error;
      await pause(backoffMs(attempt));
    }
  }
  throw new Error(`Market feed failed: ${url}`);
}
